/**
 * Личные сообщения, которые бот пишет людям.
 *
 * Тексты живут в настройках сообщества, а не в коде: правка формулировки не
 * должна требовать выкладки. Здесь описано, какие сообщения бывают, что в
 * них можно подставить и как они выглядят по умолчанию.
 *
 * Ручная рассылка сюда не входит: её текст модератор пишет каждый раз сам,
 * и шаблону там неоткуда взяться.
 */

export type NotificationId =
  | 'event.reminder'
  | 'event.notify'
  | 'event.timeChanged'
  | 'family.accepted'
  | 'family.rejected'
  | 'family.callInvite';

export interface NotificationSetting {
  /** Отправлять ли это сообщение вообще. */
  enabled: boolean;
  /** Текст с подстановками вида `{eventType}`. */
  template: string;
}

export type NotificationSettings = Record<NotificationId, NotificationSetting>;

export interface NotificationPlaceholder {
  key: string;
  description: string;
}

export interface NotificationDefinition {
  id: NotificationId;
  label: string;
  /** Когда это сообщение уходит. */
  description: string;
  placeholders: NotificationPlaceholder[];
  defaultTemplate: string;
  /**
   * Включено ли по умолчанию.
   *
   * У напоминания перед началом — нет: таким оно было и прежней настройкой.
   * Менять это заодно с переносом текстов в админку значило бы протащить
   * решение о поведении бота под видом технической правки.
   */
  defaultEnabled: boolean;
}

/**
 * Описание всех уведомлений.
 *
 * Тексты по умолчанию повторяют прежние сообщения слово в слово: смысл
 * изменения — дать возможность править их из админки, а не переписать за
 * сообщество его собственные формулировки.
 */
export const NOTIFICATIONS: readonly NotificationDefinition[] = [
  {
    id: 'event.reminder',
    label: 'Напоминание перед началом',
    description: 'Уходит участникам набора автоматически, за заданное число минут до мероприятия.',
    placeholders: [
      { key: 'eventType', description: 'Тип мероприятия, например MCL' },
      { key: 'time', description: 'Время начала' },
      { key: 'link', description: 'Ссылка на список набора' },
    ],
    defaultTemplate: [
      'Напоминание: мероприятие **{eventType}** скоро начнётся.',
      'Начало: **{time}**',
      'Список набора: {link}',
    ].join('\n'),
    // Выключено, как и прежняя настройка reminderEnabled.
    defaultEnabled: false,
  },
  {
    id: 'event.notify',
    label: 'Напоминание вручную',
    description: 'Уходит участникам набора по кнопке «Напомнить» в админке.',
    placeholders: [
      { key: 'eventType', description: 'Тип мероприятия' },
      { key: 'time', description: 'Время начала' },
      { key: 'timeLeft', description: 'Сколько осталось до начала' },
      { key: 'link', description: 'Ссылка на список набора' },
    ],
    defaultTemplate: [
      'Напоминание по мероприятию **{eventType}**.',
      'Начало: **{time}**',
      'До начала: **{timeLeft}**',
      'Список набора: {link}',
    ].join('\n'),
    defaultEnabled: true,
  },
  {
    id: 'event.timeChanged',
    label: 'Перенос времени набора',
    description: 'Уходит участникам, когда мероприятие переносят на другое время.',
    placeholders: [
      { key: 'eventType', description: 'Тип мероприятия' },
      { key: 'oldTime', description: 'Прежнее время' },
      { key: 'newTime', description: 'Новое время' },
      { key: 'link', description: 'Ссылка на список набора' },
    ],
    defaultTemplate: [
      'Время мероприятия **{eventType}** перенесено.',
      'Старое время: **{oldTime}**',
      'Новое время: **{newTime}**',
      'Список набора: {link}',
    ].join('\n'),
    defaultEnabled: true,
  },
  {
    id: 'family.accepted',
    label: 'Заявка в семью принята',
    description: 'Уходит подавшему, когда заявку принимают — из админки или кнопкой в Discord.',
    placeholders: [{ key: 'moderator', description: 'Кто принял заявку' }],
    defaultTemplate: ['Ваша заявка в семью принята.', 'Принял: {moderator}.', 'Вас ожидают на обзвоне.'].join('\n'),
    defaultEnabled: true,
  },
  {
    id: 'family.rejected',
    label: 'Заявка в семью отклонена',
    description: 'Уходит подавшему при отказе.',
    placeholders: [
      { key: 'moderator', description: 'Кто отклонил заявку' },
      { key: 'comment', description: 'Причина отказа, если указана' },
      { key: 'cooldown', description: 'Когда можно подать заявку заново' },
    ],
    defaultTemplate: [
      'Ваша заявка в семью отклонена.',
      'Рассмотрел: {moderator}.',
      'Причина: {comment}',
      '{cooldown}',
    ].join('\n'),
    defaultEnabled: true,
  },
  {
    id: 'family.callInvite',
    label: 'Приглашение на обзвон',
    description: 'Уходит подавшему по кнопке «Пригласить на обзвон». Заявка при этом остаётся в работе.',
    placeholders: [
      { key: 'moderatorMention', description: 'Упоминание модератора — ссылка на его профиль' },
      { key: 'moderator', description: 'Имя модератора' },
    ],
    defaultTemplate: [
      'Ваша заявка в семью рассмотрена.',
      'Обзвон проводит {moderatorMention} ({moderator}).',
      'Напишите ему в личные сообщения, чтобы договориться о времени.',
    ].join('\n'),
    defaultEnabled: true,
  },
];

export const DEFAULT_NOTIFICATIONS: NotificationSettings = Object.fromEntries(
  NOTIFICATIONS.map((item) => [item.id, { enabled: item.defaultEnabled, template: item.defaultTemplate }]),
) as NotificationSettings;

/**
 * Похоже ли значение на набор личных сообщений.
 *
 * Набор может быть неполным: недостающее достраивается умолчаниями при
 * чтении, благодаря чему новое уведомление появляется в коде без миграции.
 * Поэтому проверяется не состав, а форма записей.
 */
export function isNotificationSettings(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return Object.values(value as Record<string, unknown>).every((item) => {
    if (typeof item !== 'object' || item === null) {
      return false;
    }

    const setting = item as Record<string, unknown>;
    return typeof setting['enabled'] === 'boolean' && typeof setting['template'] === 'string';
  });
}

/** Подстановки, допустимые в тексте уведомления. */
export function placeholdersOf(id: NotificationId): NotificationPlaceholder[] {
  return NOTIFICATIONS.find((item) => item.id === id)?.placeholders ?? [];
}

const PLACEHOLDER = /\{([a-zA-Z]+)\}/g;

/**
 * Подставляет значения в шаблон.
 *
 * Строка, где хоть одна подстановка оказалась пустой, выпадает целиком.
 * Так было и раньше: у набора без опубликованного сообщения нет ссылки, у
 * отказа без причины нет строки «Причина», — и оставлять «Причина:» с
 * пустым хвостом было бы хуже, чем не писать её вовсе.
 */
export function renderNotification(template: string, values: Readonly<Record<string, string | null>>): string {
  return template
    .split('\n')
    .filter((line) => {
      const used = [...line.matchAll(PLACEHOLDER)].map((match) => match[1] ?? '');
      return used.every((key) => {
        const value = values[key];
        return value !== null && value !== undefined && value !== '';
      });
    })
    .map((line) => line.replaceAll(PLACEHOLDER, (_, key: string) => values[key] ?? ''))
    .join('\n')
    .trim();
}
